import { Component, OnInit, OnDestroy, ViewChild, ComponentFactoryResolver } from '@angular/core';

import { Subscription } from 'rxjs';
import { Store } from '@ngrx/store';

import { AppState, getMyAuth } from '../store/app.state';
import { PlaceholderDirective } from '../shared/directives/placeholder.directive';
import { AboutDialogComponent } from '../shared/dialogs/about-dialog/about-dialog.component';
import { ModaleUtenteComponent } from '../shared/dialogs/modale-utente/modale-utente.component';
import { ModaleCountdownComponent } from '../shared/dialogs/modale-countdown/modale-countdown.component';
import { StartKeepalive, Logout } from '../auth/store';
import { autorizzazioni } from '../shared/autorizzazioni/schema-autorizzazioni';
import { Utente } from '../shared/models/utente.model';
import { ModaleCercaGlossariComponent } from '../shared/dialogs/modale-cerca-glossari/modale-cerca-glossari.component';
import { GlossariService, cercaGlossariParent } from '../glossari/glossari.service';
import { SubSink } from 'subsink';
import { NotePersonaliComponent } from '../shared/dialogs/note-personali/note-personali.component';
import { TranslateService } from '@ngx-translate/core';

/**Componente che rappresenta l'header dell'intera applicazione */
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css']
})
export class HeaderComponent implements OnInit, OnDestroy {
  /**Subsink per la gestione delle sottoscrizioni */
  private subs = new SubSink();
  /**Booleano che definisce se l'utente è autenticato o meno */
  isAuth: boolean;
  /**Booleano che definisce se debba essere visualizzato il modale di avviso logout per inattività */
  isWarning: boolean;
  /**Uente attualmente loggato */
  loggedUser: Utente;
  /**Se è in corso il caricamento leggendo dallo stato auth */
  isLoading: boolean;
  /**Definisce se collassare il menu di testa */
  collapsed = true;

  /**Placeholder direcrive per ospitare il modale di about */
  @ViewChild(PlaceholderDirective, {static: true}) aboutHost: PlaceholderDirective;
  /**Sottoscrizione per l'evento di chiusura del modale about */
  private closeSub: Subscription;

  /**Placeholder direcrive per ospitare il modale di utente */
  @ViewChild(PlaceholderDirective, {static: true}) utenteHost: PlaceholderDirective;
  /**Sottoscrizione per l'evento di chiusura del modale utente */
  private utenteCloseSub: Subscription;

  /**Array di oggetti che memorizza le voci di menu variabili */
  menu = [];

  /**Sottoscrizione all'evento di chiusura del modale cerca glossari */
  private closeCercaGlossariSub: Subscription;
  /**Se viene visualizzato il modale cerca glossari */
  isCercaGlossariShown: boolean = false;

  /**
   * Costruttore di HeaderComponent
   * @param store {Store<AppState>} stato dell'applicazione
   * @param componentFactoryResolver {ComponentFactoryResolver} per la costruzione del modale di about
   * @param componentUtenteResolver {ComponentUtenteResolver} per la costruzione del modale di utente
   * @param glossariService {GlossariService} servizi per la sezione glossari
   */
  constructor(
    private store: Store<AppState>,
    private componentFactoryResolver: ComponentFactoryResolver,
    private componentUtenteResolver: ComponentFactoryResolver,
    private glossariService: GlossariService,
    public translate: TranslateService
  ) { }

  /**Nome dell'utente loggato */
  nome = '';
  /**Contenuto delle note personali */
  notePersonali: string;

  /**Metodo dell'interfaccia OnInit, lo uso per sottoscrivere lo stato di autenticazione, recuperare le voci di menu e gestire eventuali modali di warning */
  ngOnInit() {
    this.subs.sink = this.store.select(getMyAuth).subscribe(
      data => {
        this.isLoading = data.isLoading;
        this.loggedUser = data.loggedUser;
        this.isAuth = data.isAuth;
        if(this.isAuth) {
          this.nome = data.loggedUser.name;
          this.notePersonali = data.notePersonali;
          this.menu = [];
          for(let val in autorizzazioni) {
            let profili: string[] = autorizzazioni[val].profili;
            if(profili.includes(this.loggedUser.type)) {
              this.menu.push(autorizzazioni[val].icona);
            }
          }      
        }
        this.isWarning = data.isWarning;
        if(this.isWarning) {
          this.onShowWarning();
        }
      }
    );

    this.subs.sink = this.glossariService.getModalVisibility().subscribe(data => {
      if(data.valore) {
        this.onShowCercaGlossari(data.provenienza);  
      }
    });
  }

  setLanguage(lang: string) {
    this.translate.use(lang);
  }

  /**Metodo per la visualizzazione del modale about, gestisce anche le modalità di chiusura dello stesso */
  onShowAbout() {
    const aboutCmpFactory = this.componentFactoryResolver.resolveComponentFactory(
      AboutDialogComponent,
    );
    const hostViewContainerRef = this.aboutHost.viewContainerRef;
    hostViewContainerRef.clear();

    const componentRef = hostViewContainerRef.createComponent(aboutCmpFactory);

    this.closeSub = componentRef.instance.close.subscribe(() => {
      this.closeSub.unsubscribe();
      hostViewContainerRef.clear();
    });
  }

  /**Metodo che gestisce la visualizzazione del modale di avviso logout per inattività */
  onShowWarning() {
    const aboutCmpFactory = this.componentFactoryResolver.resolveComponentFactory(
      ModaleCountdownComponent,
    );
    const hostViewContainerRef = this.aboutHost.viewContainerRef;
    hostViewContainerRef.clear();

    const componentRef = hostViewContainerRef.createComponent(aboutCmpFactory);

    this.closeSub = componentRef.instance.close.subscribe((data:boolean) => {
      this.closeSub.unsubscribe();
      hostViewContainerRef.clear();
      if(data) {
        this.onKeepalive();
      }
    });
  }

  /**Metodo che lancia l'azione di StartKeepalive */
  onKeepalive() {
    this.store.dispatch(new StartKeepalive());
  }

  /**Metodo per la visualizzazione del modale utente, gestisce anche le modalità di chiusura dello stesso */
  onShowUtente() {
    const utenteCmpFactory = this.componentUtenteResolver.resolveComponentFactory(
      ModaleUtenteComponent,
    );
    const hostViewContainerRef = this.utenteHost.viewContainerRef;
    hostViewContainerRef.clear();

    const componentRef = hostViewContainerRef.createComponent(utenteCmpFactory);

    this.utenteCloseSub = componentRef.instance.close.subscribe(() => {
      this.utenteCloseSub.unsubscribe();
      hostViewContainerRef.clear();
    });
  }

  /**Metodo che lancia l'azione di Logout su click dell'utente */
  onLogout() {
    this.store.dispatch(new Logout());
  }

  /**
   * Metodo che gestisce la creazione del modale di cerca glossari
   * @param parent {cercaGlossariParent} da dove viene chiamato il modale di cerca glossario
   */
  onShowCercaGlossari(parent: cercaGlossariParent) {
    const aboutCmpFactory = this.componentFactoryResolver.resolveComponentFactory(
      ModaleCercaGlossariComponent
    );
    const hostViewContainerRef = this.aboutHost.viewContainerRef;
    hostViewContainerRef.clear();

    const componentRef = hostViewContainerRef.createComponent(aboutCmpFactory);
    componentRef.instance.parent = parent;

    this.closeCercaGlossariSub = componentRef.instance.close.subscribe(() => {
      this.closeCercaGlossariSub.unsubscribe();
      this.glossariService.setModalVisibility(false, parent);
      hostViewContainerRef.clear();
    });

  }

  /**Metodo che gestisce la creazione del modale di note personali */
  onShowNotePersonali() {
    const notePersonaliCmpFactory = this.componentFactoryResolver.resolveComponentFactory(NotePersonaliComponent);
    const hostViewContainerRef = this.aboutHost.viewContainerRef;
    hostViewContainerRef.clear();
    const componentRef = hostViewContainerRef.createComponent(notePersonaliCmpFactory);
    componentRef.instance.note = this.notePersonali;
    this.closeSub = componentRef.instance.close.subscribe(() => {
      this.closeSub.unsubscribe();
      hostViewContainerRef.clear();
    })
  }

  /**Metodo dell'interfaccia OnDestroy, lo usiamo per cancellare esplicitamente eventuali sottoscrizioni */
  ngOnDestroy() {
    this.subs.unsubscribe();
    if(this.closeSub) {
      this.closeSub.unsubscribe();
    }
    if(this.utenteCloseSub) {
      this.utenteCloseSub.unsubscribe();
    }
  }

}
